import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ArrowDown2,
    ArrowLeft2,
    MagicStar,
    MessageText,
    TickSquare,
} from "iconsax-react";
import { ToastContext } from "../../../component/global/ToastContext";
import { useRequest } from "../../../hooks/helperData/useRequest";
import { REPORT_AI_SUGGESTIONS } from "../../../helperData/ApiAddress";
import {
    AiCoinHint,
    isInsufficientCoins,
    useAiCoinGate,
} from "../../../features/ai-billing/AiCoinGate";
import AiInsightBody from "../../../features/ai-insight/AiInsightBody";
import {
    formatSuggestionTime,
    messageFromSuggestionError,
    normalizeSuggestionPayload,
} from "../suggestionActions";

function SuggestionCards({ items, idPrefix, t }) {
    return (
        <ul className="ai-action-list">
            {items.map((item) => (
                <li key={`${idPrefix}-${item.id}`} className="ai-action-card">
                    <span className={`ai-action-badge is-${item.priority}`}>
                        {t(`ai.actions.priority.${item.priority}`)}
                    </span>
                    <div className="ai-action-copy">
                        <h3>{item.title}</h3>
                        <p>{item.body}</p>
                        {item.rationale ? (
                            <p className="ai-action-rationale">
                                {item.rationale}
                            </p>
                        ) : null}
                        <span className="ai-action-category">
                            {t(`ai.actions.category.${item.category}`, {
                                defaultValue: item.category,
                            })}
                        </span>
                    </div>
                    <Link className="ai-action-link" to={item.href}>
                        {t("ai.actions.view")}
                        <ArrowLeft2 size={14} aria-hidden="true" />
                    </Link>
                </li>
            ))}
        </ul>
    );
}

function historyLabel(entry) {
    return (
        entry.headline ||
        entry.suggestions[0]?.title ||
        ""
    );
}

export default function AiSuggestedActions({ setup, embedded }) {
    const { t } = useTranslation();
    const toast = useContext(ToastContext);
    const [payload, setPayload] = useState(null);
    const [loadingStored, setLoadingStored] = useState(true);
    const [openHistoryIds, setOpenHistoryIds] = useState(() => new Set());
    const [getStored] = useRequest(REPORT_AI_SUGGESTIONS, "GET");
    const [generateAi, generating] = useRequest(REPORT_AI_SUGGESTIONS, "POST");
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    useEffect(() => {
        let cancelled = false;
        getStored()
            .then((data) => {
                if (!cancelled) setPayload(data);
            })
            .catch(() => {
                if (!cancelled) setPayload(null);
            })
            .finally(() => {
                if (!cancelled) setLoadingStored(false);
            });
        return () => {
            cancelled = true;
        };
        // Session-scoped; fetch once when the tab mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const normalized = normalizeSuggestionPayload(payload || {});
    const canGenerate =
        payload?.can_generate ?? setup.canSuggest ?? setup.hasDecisionData;
    const showProfileActions = !canGenerate;
    const profileActions = setup.incompleteStages;
    const busy = setup.loading || loadingStored || generating;
    const generatedAt = formatSuggestionTime(normalized.generatedAt);

    const toggleHistory = (id) => {
        setOpenHistoryIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const generate = () => {
        if (!canGenerate || generating) return;
        confirmAndRun("org_suggestions", () => {
            generateAi({})
                .then((data) => {
                    setPayload(data);
                    setOpenHistoryIds(new Set());
                })
                .catch((error) => {
                    if (isInsufficientCoins(error)) {
                        openFromError(error);
                        return;
                    }
                    toast(messageFromSuggestionError(error, t), "danger");
                });
        });
    };

    return (
        <section
            className={`ai-actions${embedded ? " ai-actions--page" : ""}`}
            aria-labelledby="ai-actions-title"
        >
            <div className="ai-actions-header">
                <div className="ai-actions-heading">
                    <span className="ai-actions-icon" aria-hidden="true">
                        <MagicStar size={20} variant="Bold" />
                    </span>
                    <div>
                        <h2 id="ai-actions-title" className="report-header">
                            {t(
                                embedded
                                    ? "reports.tab.ai"
                                    : "ai.actions.title"
                            )}
                        </h2>
                        <p className="ai-actions-lead">
                            {showProfileActions
                                ? t("ai.actions.empty.lead")
                                : t("ai.actions.has.data.lead")}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    className="ai-actions-generate"
                    onClick={generate}
                    disabled={!canGenerate || generating || loadingStored}
                    aria-busy={generating}
                >
                    <MagicStar size={16} aria-hidden="true" />
                    {generating
                        ? t("ai.actions.generating")
                        : normalized.suggestions.length
                        ? t("ai.actions.refresh")
                        : t("ai.actions.generate")}
                    <AiCoinHint operation="org_suggestions" />
                </button>
            </div>
            <div className="ai-actions-body" aria-live="polite">
                {busy ? (
                    <div className="ai-actions-skeleton" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                    </div>
                ) : showProfileActions ? (
                    profileActions.length ? (
                        <ul className="ai-action-list">
                            {profileActions.map((stage) => (
                                <li key={stage.id} className="ai-action-card">
                                    <span
                                        className={`ai-action-mark ${
                                            stage.complete ? "is-done" : ""
                                        }`}
                                        aria-hidden="true"
                                    >
                                        {stage.complete ? (
                                            <TickSquare
                                                size={16}
                                                variant="Bold"
                                            />
                                        ) : (
                                            <MessageText size={16} />
                                        )}
                                    </span>
                                    <div className="ai-action-copy">
                                        <h3>{t(stage.labelKey)}</h3>
                                        <p>{t("ai.actions.profile.step")}</p>
                                    </div>
                                    <Link
                                        className="ai-action-link"
                                        to={stage.href}
                                    >
                                        {t("setup.cta.complete")}
                                        <ArrowLeft2
                                            size={14}
                                            aria-hidden="true"
                                        />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="ai-actions-placeholder">
                            {t("ai.actions.need.activity")}
                        </p>
                    )
                ) : normalized.suggestions.length ? (
                    <>
                        <p className="ai-actions-current-label">
                            {t("ai.actions.latest")}
                        </p>
                        <AiInsightBody
                            headline={normalized.headline}
                            metaItems={
                                generatedAt
                                    ? [
                                          {
                                              id: "generated",
                                              text: t(
                                                  "ai.actions.generated.at",
                                                  { date: generatedAt }
                                              ),
                                          },
                                      ]
                                    : []
                            }
                        >
                            <SuggestionCards
                                items={normalized.suggestions}
                                idPrefix="latest"
                                t={t}
                            />
                        </AiInsightBody>
                        {normalized.history.length > 0 ? (
                            <section
                                className="ai-actions-history"
                                aria-labelledby="ai-actions-history-title"
                            >
                                <h3 id="ai-actions-history-title">
                                    {t("ai.actions.previous")}
                                </h3>
                                <ul className="ai-actions-history__list">
                                    {normalized.history.map((entry) => {
                                        const open = openHistoryIds.has(
                                            entry.id
                                        );
                                        const panelId = `ai-history-${entry.id}`;
                                        const dateLabel = formatSuggestionTime(
                                            entry.generatedAt
                                        );
                                        const title = historyLabel(entry);
                                        return (
                                            <li
                                                key={entry.id}
                                                className="ai-actions-history__item"
                                            >
                                                <button
                                                    type="button"
                                                    className="ai-actions-history__trigger"
                                                    aria-expanded={open}
                                                    aria-controls={panelId}
                                                    onClick={() =>
                                                        toggleHistory(entry.id)
                                                    }
                                                >
                                                    {dateLabel ? (
                                                        <span className="ai-actions-history__date">
                                                            {dateLabel}
                                                        </span>
                                                    ) : null}
                                                    <span className="ai-actions-history__copy">
                                                        {title}
                                                    </span>
                                                    <span className="ai-actions-history__count">
                                                        {t("ai.actions.count", {
                                                            count: entry
                                                                .suggestions
                                                                .length,
                                                        })}
                                                    </span>
                                                    <ArrowDown2
                                                        size={16}
                                                        className={`ai-actions-history__chevron${
                                                            open
                                                                ? " is-open"
                                                                : ""
                                                        }`}
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                                {open ? (
                                                    <div
                                                        id={panelId}
                                                        className="ai-actions-history__panel"
                                                    >
                                                        <AiInsightBody
                                                            tone="compact"
                                                            headline={
                                                                entry.headline
                                                            }
                                                        >
                                                            <SuggestionCards
                                                                items={
                                                                    entry.suggestions
                                                                }
                                                                idPrefix={
                                                                    entry.id
                                                                }
                                                                t={t}
                                                            />
                                                        </AiInsightBody>
                                                    </div>
                                                ) : null}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </section>
                        ) : null}
                    </>
                ) : (
                    <p className="ai-actions-placeholder">
                        {t("ai.actions.ready")}
                    </p>
                )}
            </div>
            {gate}
        </section>
    );
}
